import { Box, Typography } from "@mui/material";
import { useMutation } from "convex/react";
import { AnimatePresence, motion } from "framer-motion";
import { Disc3, Lock, MailCheck, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { api } from "../../../convex/_generated/api";
import { useAnalytics } from "@/hooks/useAnalytics";
import { getDeviceInfo, getVisitorId, getVisitorLocation } from "@/lib/analyticsClient";
import { gatePrompt, gatedPageKind, gatedPageLabel } from "@/utils/artistGate";
import { UNLOCK_LINK_SENT_MESSAGE, describeArtistGateOutcome, needsUnlockLink, type SubscribeOutcome, type UnlockPhase } from "@/utils/mailingUnlock";
import { getOptimizedImageUrl } from "../_helpers";
import { forgetSubscribedEmail, getUnlockToken, rememberArtistGateDismissed, wasArtistGateDismissed } from "../mailing/mailingStorage";
import SubscribeForm from "../mailing/SubscribeForm";
import TruevinedSVGLogo from "../truevinedSVGLogo";
import { useFocusTrap } from "./useFocusTrap";

export interface GateArtist {
  name: string;
  image?: string;
  followers?: number;
  genres?: string[];
}

interface ArtistGateScreenProps {
  slug: string;
  artist: GateArtist;
  phase: UnlockPhase;
  storedEmail: string | null;
  onEmailChange: (email: string | null) => void;
}

const INK = "#0b0b0d";
const PAPER = "var(--textColor, #f3eee4)";
const ACCENT = "var(--logoColor, #d8c49a)";
const CARD = "rgba(20,20,24,0.9)";

export const formatFollowers = (total?: number): string | null => {
  if (!total || total <= 0) return null;
  if (total >= 1_000_000) return `${(total / 1_000_000).toFixed(1).replace(/\.0$/, "")}M listeners on Spotify`;
  if (total >= 1_000) return `${(total / 1_000).toFixed(1).replace(/\.0$/, "")}K listeners on Spotify`;
  return `${total} listeners on Spotify`;
};

const linkButtonSx = {
  background: "none",
  border: 0,
  p: 0,
  minHeight: "44px",
  display: "inline-flex",
  alignItems: "center",
  color: ACCENT,
  fontFamily: "Poppins",
  fontWeight: 700,
  fontSize: { xs: "15px", md: "0.85rem" },
  textDecoration: "underline",
  textUnderlineOffset: "4px",
  cursor: "pointer",
  transition: "opacity 160ms ease",
  "&:hover:not(:disabled)": { opacity: 0.78 },
  "&:disabled": { opacity: 0.55, cursor: "wait" },
  "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: "4px", borderRadius: "4px" },
} as const;

const primaryButtonSx = {
  border: 0,
  minHeight: "52px",
  px: "26px",
  borderRadius: "999px",
  backgroundColor: ACCENT,
  color: INK,
  fontFamily: "Poppins",
  fontWeight: 700,
  fontSize: { xs: "16px", md: "0.9rem" },
  letterSpacing: "0.01em",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  cursor: "pointer",
  transition: "transform 140ms cubic-bezier(0.23, 1, 0.32, 1), filter 160ms ease",
  "&:hover": { filter: "brightness(1.08)" },
  "&:active": { transform: "scale(0.97)" },
  "&:focus-visible": { outline: `3px solid color-mix(in srgb, ${ACCENT} 55%, white)`, outlineOffset: "3px" },
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
} as const;

function TeaserRows() {
  return (
    <Box aria-hidden sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px", mt: "4px" }}>
      {[0, 1, 2, 3].map((row) => (
        <Box
          key={row}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            opacity: 0.9 - row * 0.18,
            filter: `blur(${1.5 + row * 1.1}px)`,
          }}
        >
          <Box sx={{ width: "34px", height: "34px", borderRadius: "8px", backgroundColor: "rgba(255,255,255,0.13)", flexShrink: 0 }} />
          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
            <Box sx={{ height: "9px", borderRadius: "999px", width: `${74 - row * 9}%`, backgroundColor: "rgba(255,255,255,0.15)" }} />
            <Box sx={{ height: "7px", borderRadius: "999px", width: `${48 - row * 7}%`, backgroundColor: "rgba(255,255,255,0.09)" }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

interface PendingPanelProps {
  email: string | null;
  message: string | null;
  behind: string;
  resending: boolean;
  onResend: () => void;
  onChangeEmail: () => void;
}

function PendingPanel({ email, message, behind, resending, onResend, onChangeEmail }: PendingPanelProps) {
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Box
          aria-hidden
          sx={{
            width: "36px",
            height: "36px",
            borderRadius: "999px",
            display: "grid",
            placeItems: "center",
            backgroundColor: `color-mix(in srgb, ${ACCENT} 18%, transparent)`,
            color: ACCENT,
            flexShrink: 0,
          }}
        >
          <MailCheck style={{ width: "18px", height: "18px" }} />
        </Box>
        <Typography
          component="h2"
          sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: { xs: "19px", md: "1.1rem" }, m: 0, color: PAPER }}
        >
          Check your inbox
        </Typography>
      </Box>
      <Typography
        aria-live="polite"
        sx={{ fontFamily: "Poppins", fontSize: { xs: "15px", md: "0.95rem" }, lineHeight: 1.65, opacity: 0.88, m: 0 }}
      >
        {message ?? `We sent a confirmation link to ${email}. Click it and ${behind} loads.`}
      </Typography>
      <Typography sx={{ fontFamily: "Poppins", fontSize: { xs: "13px", md: "0.8rem" }, opacity: 0.58, lineHeight: 1.6, m: 0 }}>
        Leave this tab open. The page opens on its own the second you confirm.
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
        <Box component="button" type="button" onClick={onResend} disabled={resending} sx={linkButtonSx}>
          {resending ? "Sending..." : "Resend the link"}
        </Box>
        <Box component="button" type="button" onClick={onChangeEmail} sx={linkButtonSx}>
          Use a different email
        </Box>
      </Box>
    </>
  );
}

export function ArtistGateScreen({ slug, artist, phase, storedEmail, onEmailChange }: ArtistGateScreenProps) {
  const location = useLocation();
  const { track } = useAnalytics();
  const subscribe = useMutation(api.mailingList.subscribe);
  const requestUnlockLink = useMutation(api.mailingList.requestUnlockLink);
  const [dismissed, setDismissed] = useState(() => wasArtistGateDismissed(slug));
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const shownRef = useRef<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const reopenRef = useRef<HTMLButtonElement>(null);

  const prompt = gatePrompt({ phase, dismissed });
  const modalOpen = prompt === "modal";

  const artworkUrl = artist.image ? getOptimizedImageUrl(artist.image, { width: 640, quality: 80 }) : undefined;
  const followerLine = formatFollowers(artist.followers);
  const genre = artist.genres?.[0];
  const behind = useMemo(() => gatedPageLabel(location.pathname, artist.name), [location.pathname, artist.name]);

  useEffect(() => {
    setDismissed(wasArtistGateDismissed(slug));
  }, [slug]);

  useEffect(() => {
    const key = `${slug}:${location.pathname}`;
    if (shownRef.current === key) return;
    shownRef.current = key;
    if (getDeviceInfo().isBot) return;
    track("gate_shown", {
      eventCategory: "mailing",
      artist: slug,
      metadata: { pagePath: location.pathname, pageKind: gatedPageKind(location.pathname), phase, prompt },
    });
  }, [location.pathname, phase, prompt, slug, track]);

  const closeModal = useCallback(() => {
    rememberArtistGateDismissed(slug);
    setDismissed(true);
    if (!getDeviceInfo().isBot) {
      track("gate_dismissed", { eventCategory: "mailing", artist: slug, metadata: { pagePath: location.pathname } });
    }
    window.setTimeout(() => reopenRef.current?.focus(), 0);
  }, [location.pathname, slug, track]);

  const openModal = useCallback(() => {
    setDismissed(false);
    setJustSent(false);
  }, []);

  useFocusTrap(dialogRef, modalOpen, closeModal);

  const sendUnlockLink = useCallback(
    async (email: string) => {
      const device = getDeviceInfo();
      const geo = await getVisitorLocation();
      await requestUnlockLink({
        email,
        pagePath: location.pathname,
        visitorId: getVisitorId(),
        country: geo?.country,
        deviceType: device.deviceType,
      });
      setPendingMessage(`${UNLOCK_LINK_SENT_MESSAGE} ${email}. Open it here and the page unlocks.`);
    },
    [location.pathname, requestUnlockLink],
  );

  const handleSubscribed = useCallback(
    (outcome: SubscribeOutcome) => {
      onEmailChange(outcome.email);
      setJustSent(true);
      if (needsUnlockLink({ status: outcome.status, hasUnlockToken: Boolean(getUnlockToken()) })) {
        setPendingMessage(describeArtistGateOutcome(outcome).message);
        void sendUnlockLink(outcome.email);
        return;
      }
      setPendingMessage(describeArtistGateOutcome(outcome).message);
    },
    [onEmailChange, sendUnlockLink],
  );

  const handleResend = useCallback(async () => {
    if (!storedEmail || resending) return;
    setResending(true);
    try {
      const device = getDeviceInfo();
      const geo = await getVisitorLocation();
      const result = await subscribe({
        email: storedEmail,
        source: "artist_gate",
        artist: slug,
        pagePath: location.pathname,
        visitorId: getVisitorId(),
        country: geo?.country,
        deviceType: device.deviceType,
      });
      setPendingMessage(describeArtistGateOutcome(result).message);
      if (needsUnlockLink({ status: result.status, hasUnlockToken: Boolean(getUnlockToken()) })) await sendUnlockLink(result.email);
    } catch (err) {
      const raw = err instanceof Error ? err.message.replace(/^.*?ConvexError:\s*/, "").split("\n")[0] : "";
      setPendingMessage(!raw || raw.includes("Uncaught") ? "That link would not send. Try again in a minute." : raw);
    } finally {
      setResending(false);
    }
  }, [location.pathname, resending, slug, storedEmail, subscribe]);

  const handleChangeEmail = useCallback(() => {
    forgetSubscribedEmail();
    onEmailChange(null);
    setPendingMessage(null);
    setJustSent(false);
    setDismissed(false);
  }, [onEmailChange]);

  return (
    <Box
      component="main"
      aria-labelledby="artist-gate-title"
      sx={{
        position: "relative",
        minHeight: "100dvh",
        backgroundColor: INK,
        color: PAPER,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        px: { xs: "22px", md: "2rem" },
        py: { xs: "56px", md: "4rem" },
      }}
    >
      {artworkUrl && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: "-12%",
            backgroundImage: `url(${artworkUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(60px) saturate(1.25)",
            opacity: 0.5,
            transform: "scale(1.15)",
          }}
        />
      )}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(120% 90% at 50% 0%, rgba(11,11,13,0.24) 0%, rgba(11,11,13,0.78) 55%, ${INK} 100%)`,
        }}
      />

      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.23, 1, 0.32, 1] }}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: { xs: "100%", md: "560px" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: { xs: "20px", md: "1.1rem" },
        }}
      >
        <Box
          component={RouterLink}
          to="/"
          aria-label="Truevined home"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "44px",
            opacity: 0.72,
            transition: "opacity 180ms ease",
            "&:hover": { opacity: 1 },
            "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: "6px" },
            "& .truevined-svg-logo svg": { width: { xs: "72px", md: "5rem" }, height: "auto" },
          }}
        >
          <TruevinedSVGLogo color="var(--logoColor, #d8c49a)" alt="Truevined" />
        </Box>

        {artworkUrl && (
          <Box
            sx={{
              position: "relative",
              width: { xs: "132px", md: "164px" },
              height: { xs: "132px", md: "164px" },
              borderRadius: "50%",
              padding: "3px",
              background: `conic-gradient(from 210deg, color-mix(in srgb, ${ACCENT} 75%, transparent), rgba(255,255,255,0.08) 55%, color-mix(in srgb, ${ACCENT} 55%, transparent))`,
              boxShadow: "0 26px 70px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.05)",
            }}
          >
            <Box
              component="img"
              src={artworkUrl}
              alt=""
              aria-hidden
              width={640}
              height={640}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "50%",
                display: "block",
                backgroundColor: "rgba(255,255,255,0.05)",
              }}
            />
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                right: "-2px",
                bottom: "4px",
                width: "42px",
                height: "42px",
                borderRadius: "999px",
                display: "grid",
                placeItems: "center",
                backgroundColor: INK,
                border: `1px solid color-mix(in srgb, ${ACCENT} 42%, transparent)`,
                color: ACCENT,
              }}
            >
              <Lock style={{ width: "18px", height: "18px" }} />
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "8px", md: "0.5rem" }, alignItems: "center" }}>
          <Typography
            id="artist-gate-title"
            component="h1"
            sx={{
              fontFamily: "Hanson, Poppins, sans-serif",
              fontSize: { xs: "34px", md: "3rem" },
              lineHeight: 1.06,
              letterSpacing: "0.02em",
              m: 0,
              color: PAPER,
            }}
          >
            {artist.name}
          </Typography>
          {(followerLine || genre) && (
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontSize: { xs: "13px", md: "0.85rem" },
                fontWeight: 500,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                opacity: 0.68,
                m: 0,
              }}
            >
              {[genre, followerLine].filter(Boolean).join(" · ")}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            width: "100%",
            borderRadius: "20px",
            border: `1px solid color-mix(in srgb, ${ACCENT} 26%, transparent)`,
            backgroundColor: CARD,
            backdropFilter: "blur(18px)",
            p: { xs: "22px 20px", md: "1.6rem 1.7rem" },
            display: "flex",
            flexDirection: "column",
            gap: { xs: "14px", md: "0.9rem" },
            textAlign: "left",
            boxShadow: "0 28px 70px rgba(0,0,0,0.5)",
          }}
        >
          {prompt === "pending" ? (
            <PendingPanel
              email={storedEmail}
              message={pendingMessage}
              behind={behind}
              resending={resending}
              onResend={() => void handleResend()}
              onChangeEmail={handleChangeEmail}
            />
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box
                  aria-hidden
                  sx={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "999px",
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: `color-mix(in srgb, ${ACCENT} 18%, transparent)`,
                    color: ACCENT,
                    flexShrink: 0,
                  }}
                >
                  <Disc3 style={{ width: "18px", height: "18px" }} />
                </Box>
                <Typography
                  component="h2"
                  sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: { xs: "17px", md: "1.05rem" }, m: 0, color: PAPER }}
                >
                  Inside: {behind}
                </Typography>
              </Box>
              <TeaserRows />
              <Box
                ref={reopenRef}
                component="button"
                type="button"
                onClick={openModal}
                sx={{ ...primaryButtonSx, alignSelf: "flex-start", mt: "6px", visibility: modalOpen ? "hidden" : "visible" }}
              >
                <Sparkles style={{ width: "17px", height: "17px" }} aria-hidden />
                Unlock {artist.name}
              </Box>
            </>
          )}
        </Box>

        <Box
          component={RouterLink}
          to="/"
          sx={{ ...linkButtonSx, fontWeight: 600, opacity: 0.78, "&:hover": { opacity: 1 } }}
        >
          Listen to Truevined instead
        </Box>
      </Box>

      <AnimatePresence>
        {modalOpen ? (
          <Box
            component={motion.div}
            key="gate-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            onClick={closeModal}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 10030,
              display: "flex",
              justifyContent: "center",
              alignItems: { xs: "flex-end", md: "center" },
              px: { xs: 0, md: "2rem" },
              py: { xs: 0, md: "3rem" },
              backgroundColor: "rgba(6,6,8,0.46)",
              backdropFilter: "blur(2px)",
              overflowY: "auto",
            }}
          >
            <Box
              ref={dialogRef}
              component={motion.div}
              role="dialog"
              aria-modal="true"
              aria-labelledby="artist-gate-modal-title"
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.99 }}
              transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
              onClick={(event: ReactMouseEvent) => event.stopPropagation()}
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: { xs: "100%", md: "440px" },
                mt: { xs: 0, md: "clamp(0px, 13vh, 130px)" },
                borderRadius: { xs: "24px 24px 0 0", md: "22px" },
                border: `1px solid color-mix(in srgb, ${ACCENT} 32%, transparent)`,
                borderBottom: { xs: "none", md: `1px solid color-mix(in srgb, ${ACCENT} 32%, transparent)` },
                backgroundColor: "rgba(16,16,20,0.97)",
                color: PAPER,
                p: { xs: "26px 22px calc(26px + env(safe-area-inset-bottom))", md: "1.8rem 1.75rem" },
                display: "flex",
                flexDirection: "column",
                gap: { xs: "14px", md: "0.9rem" },
                boxShadow: "0 40px 110px rgba(0,0,0,0.7)",
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={closeModal}
                aria-label="Close and keep looking"
                sx={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  width: "44px",
                  height: "44px",
                  display: "grid",
                  placeItems: "center",
                  border: 0,
                  borderRadius: "999px",
                  background: "none",
                  color: PAPER,
                  opacity: 0.65,
                  cursor: "pointer",
                  transition: "opacity 160ms ease, background-color 160ms ease",
                  "&:hover": { opacity: 1, backgroundColor: "rgba(255,255,255,0.08)" },
                  "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: "2px", opacity: 1 },
                }}
              >
                <X style={{ width: "19px", height: "19px" }} aria-hidden />
              </Box>

              {justSent ? (
                <PendingPanel
                  email={storedEmail}
                  message={pendingMessage}
                  behind={behind}
                  resending={resending}
                  onResend={() => void handleResend()}
                  onChangeEmail={handleChangeEmail}
                />
              ) : (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", pr: "44px" }}>
                    <Box
                      aria-hidden
                      sx={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "999px",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: `color-mix(in srgb, ${ACCENT} 18%, transparent)`,
                        color: ACCENT,
                        flexShrink: 0,
                      }}
                    >
                      <Lock style={{ width: "18px", height: "18px" }} />
                    </Box>
                    <Typography
                      id="artist-gate-modal-title"
                      component="h2"
                      sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: { xs: "19px", md: "1.15rem" }, m: 0, color: PAPER }}
                    >
                      Unlock {artist.name}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: "Poppins", fontSize: { xs: "15px", md: "0.95rem" }, lineHeight: 1.65, opacity: 0.88, m: 0 }}>
                    Truevined keeps {behind} for people on the list. Drop your email, click the link we send, and it loads. One
                    confirmation opens every artist here, not just this one.
                  </Typography>
                  <SubscribeForm
                    source="artist_gate"
                    artist={slug}
                    buttonLabel="Send the link"
                    compact
                    accentColor={ACCENT}
                    inkColor={INK}
                    initialEmail={storedEmail ?? ""}
                    onSubscribed={handleSubscribed}
                  />
                  <Typography sx={{ fontFamily: "Poppins", fontSize: { xs: "13px", md: "0.78rem" }, opacity: 0.56, lineHeight: 1.6, m: 0 }}>
                    One email to confirm, then new music when it lands. Every message carries a one click way out.
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        ) : null}
      </AnimatePresence>
    </Box>
  );
}

export default ArtistGateScreen;
